// Normaliza qualquer representacao de unidade para um id consistente.
export const normalizeUnitId = (unitValue) => {
  if (unitValue === undefined || unitValue === null) return null;

  if (typeof unitValue === 'object') {
    const nested = unitValue?.id ?? unitValue?.unit;
    return normalizeUnitId(nested);
  }

  const raw = String(unitValue).trim();
  if (!raw || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'undefined') {
    return null;
  }

  const asNumber = Number(raw);
  return Number.isNaN(asNumber) ? raw : asNumber;
};

const mapUnitOption = (rawUnit, fallbackUserInfo = null) => {
  const normalizedId = normalizeUnitId(rawUnit);
  if (normalizedId === null) return null;

  return {
    id: normalizedId,
    name:
      rawUnit?.name ||
      rawUnit?.unit_name ||
      fallbackUserInfo?.unit_name ||
      `Unidade ${normalizedId}`,
    acronym:
      rawUnit?.acronym ||
      rawUnit?.unit_acronym ||
      rawUnit?.sigla ||
      fallbackUserInfo?.unit_acronym ||
      '',
  };
};

// Normaliza lista de unidades do usuario para um array unico e consistente.
export const getUserUnits = (userInfo) => {
  const rawUnits = Array.isArray(userInfo?.units) && userInfo.units.length
    ? userInfo.units
    : (userInfo?.unit !== undefined && userInfo?.unit !== null ? [userInfo.unit] : []);

  const seen = new Set();
  return rawUnits
    .map((unit) => mapUnitOption(unit, userInfo))
    .filter((unit) => {
      if (!unit || seen.has(String(unit.id))) return false;
      seen.add(String(unit.id));
      return true;
    });
};

export const getUserUnitIds = (userInfo) => getUserUnits(userInfo).map((unit) => unit.id);

// Mantido por compatibilidade com codigo legado.
export const getUserUnitId = (userInfo) => {
  const unitIds = getUserUnitIds(userInfo);
  return unitIds.length ? unitIds[0] : null;
};

// Usuario com escopo fixo: possui exatamente uma unidade.
export const hasFixedUnit = (userInfo) => getUserUnitIds(userInfo).length === 1;
export const hasMultipleUnits = (userInfo) => getUserUnitIds(userInfo).length > 1;
export const isGlobalUnitUser = (userInfo) => getUserUnitIds(userInfo).length === 0;

export const isUnitAllowedForUser = (userInfo, unitId) => {
  const normalized = normalizeUnitId(unitId);
  if (normalized === null) return false;
  return getUserUnitIds(userInfo).some((id) => String(id) === String(normalized));
};

// Resolve unidade ativa padrao para o usuario.
export const resolveDefaultActiveUnitId = (userInfo, currentActiveUnitId = null) => {
  const normalizedCurrent = normalizeUnitId(currentActiveUnitId);
  const unitIds = getUserUnitIds(userInfo);

  if (!unitIds.length) return normalizedCurrent;

  if (normalizedCurrent !== null) {
    const isCurrentAllowed = unitIds.some((id) => String(id) === String(normalizedCurrent));
    if (isCurrentAllowed) return normalizedCurrent;
  }

  return unitIds[0];
};

// Unidade efetiva: unica unidade do usuario ou unidade ativa selecionada.
export const getEffectiveUnitId = (userInfo, activeUnitId = null) => {
  const unitIds = getUserUnitIds(userInfo);
  if (unitIds.length === 1) return unitIds[0];

  const normalizedActive = normalizeUnitId(activeUnitId);
  if (!unitIds.length) return normalizedActive;

  const isActiveAllowed = unitIds.some((id) => String(id) === String(normalizedActive));
  return isActiveAllowed ? normalizedActive : null;
};

// Para leitura: envia sempre a unidade efetiva quando houver.
export const buildReadParams = (params = {}, userInfo, activeUnitId = null) => {
  const baseParams = { ...(params || {}) };
  const effectiveUnitId = getEffectiveUnitId(userInfo, activeUnitId);

  if (effectiveUnitId === null) {
    delete baseParams.unit;
    return baseParams;
  }

  return {
    ...baseParams,
    unit: effectiveUnitId,
  };
};

// Para escrita: obriga unidade efetiva antes de salvar.
export const requireWriteUnit = (userInfo, activeUnitId = null) => {
  const effectiveUnitId = getEffectiveUnitId(userInfo, activeUnitId);
  if (effectiveUnitId === null) {
    return {
      ok: false,
      unitId: null,
      message: 'Selecione uma unidade para concluir esta operação.',
    };
  }

  return {
    ok: true,
    unitId: effectiveUnitId,
    message: '',
  };
};
